import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { plural } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { LedgerEntry, PayoutDestination, PayoutSub } from '../api/models'
import { PAYOUT_METHODS, type MethodId } from '../api/payoutMethods'
import { PayoutClaimCta } from './PayoutClaimCta'
import { PayoutCollectCta } from './PayoutCollectCta'
import { PayoutInFlightExpand } from './PayoutInFlightExpand'
import { PayoutStatusBadge } from './PayoutStatusBadge'

const formatDateTime = (iso: string) => dayjs(iso).format('ll · HH:mm')

const truncateAddress = (address: string) =>
  address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address

const railName = (payout?: PayoutSub): string | undefined => PAYOUT_METHODS[payout?.method as MethodId]?.shortName

const railDestination = (destination: PayoutDestination, rail: string | undefined): React.ReactNode => {
  const prefix = rail ? `${rail} · ` : ''
  if (destination.kind === 'crypto') {
    return (
      <Tooltip>
        <TooltipTrigger className="text-left">
          <span className="text-body-3 text-gray-90 block truncate">
            {prefix}
            <span className="font-medium">{truncateAddress(destination.walletAddress)}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="break-all">{destination.walletAddress}</span>
        </TooltipContent>
      </Tooltip>
    )
  }
  if (destination.kind === 'stripe') {
    return (
      <Tooltip>
        <TooltipTrigger className="text-left">
          <span className="text-body-3 text-gray-90 block truncate">
            {prefix}
            <span className="font-medium">
              {destination.country} · {truncateAddress(destination.stripeAccountId)}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="break-all">{destination.stripeAccountId}</span>
        </TooltipContent>
      </Tooltip>
    )
  }
  if (destination.kind === 'tremendous') {
    return (
      <span className="text-body-3 text-gray-90 block truncate">
        <span className="font-medium">{rail}</span>
      </span>
    )
  }
  return (
    <span className="text-body-3 text-gray-90 block truncate">
      {prefix}
      <span className="font-medium">{destination.recipientEmail}</span>
    </span>
  )
}

const EXPIRY_WARNING_DAYS = 30

const expiryElement = (expiresAt: string | null | undefined): React.ReactNode => {
  if (expiresAt == null) return null
  const expiry = dayjs(expiresAt)
  const now = dayjs()
  if (!expiry.isAfter(now)) return null
  const daysUntilExpiry = expiry.diff(now, 'day', true)
  if (daysUntilExpiry > EXPIRY_WARNING_DAYS) {
    return (
      <span className="text-body-3 text-gray-90 block truncate">
        <Trans>Expires {expiry.format('D MMM YYYY')}</Trans>
      </span>
    )
  }
  const daysLeft = Math.ceil(daysUntilExpiry)
  return (
    <span className="text-body-3 text-orange block truncate font-medium">
      {plural(daysLeft, { one: 'Expires in # day', other: 'Expires in # days' })}
    </span>
  )
}

const isExpandable = (entry: LedgerEntry) =>
  entry.kind === 'PAYOUT_DEBIT' && entry.payout?.destination.kind === 'crypto'

type RowVisuals = {
  icon: React.ReactNode
  label: React.ReactNode
  subtitle: React.ReactNode
  sign: '+' | '−'
  statusPill?: React.ReactNode
}

const renderVisuals = (entry: LedgerEntry): RowVisuals => {
  if (entry.kind === 'EARNING_CREDIT') {
    return {
      icon: (
        <div
          className="flex h-10 w-10 items-center justify-center rounded-full bg-(--colorGreen)/10 text-(--colorGreen)"
          aria-hidden
        >
          <ArrowDownLeft size={20} />
        </div>
      ),
      label: <Trans>Earning</Trans>,
      subtitle: entry.earning ? (
        <span className="text-body-3 text-gray-90 truncate">
          <Trans>Mission: {entry.earning.projectName}</Trans>
        </span>
      ) : null,
      sign: '+'
    }
  }

  if (entry.kind === 'PAYOUT_DEBIT') {
    const payout = entry.payout
    const destination = payout?.destination
    let subtitle: React.ReactNode = null
    if (destination) {
      const needsClaim = payout?.requiresUserAction === true && payout.actionReason === 'CLAIM_ON_PAYPAL'
      // An equality check on an optional field: widening ActionReason does NOT force this branch
      // at compile time. Omit it and the collect button silently never appears.
      const needsCollect = payout?.requiresUserAction === true && payout.actionReason === 'COLLECT_ON_TREMENDOUS'
      subtitle = (
        <>
          {railDestination(destination, railName(payout))}
          {destination.kind === 'tremendous' && expiryElement(payout?.expiresAt)}
          {needsClaim && <PayoutClaimCta />}
          {needsCollect && payout && <PayoutCollectCta payoutId={payout.id} />}
        </>
      )
    }
    return {
      icon: (
        <div className="text-orange bg-orange/10 flex h-10 w-10 items-center justify-center rounded-full" aria-hidden>
          <ArrowUpRight size={20} />
        </div>
      ),
      label: <Trans>Payout</Trans>,
      subtitle,
      sign: '−',
      statusPill: payout ? <PayoutStatusBadge status={payout.status} /> : undefined
    }
  }

  if (entry.kind === 'PAYOUT_REFUND') {
    const payout = entry.payout
    const destination = payout?.destination
    let subtitle: React.ReactNode
    if (destination) {
      subtitle = railDestination(destination, railName(payout))
    } else {
      subtitle = (
        <span className="text-body-3 text-gray-90">
          <Trans>Couldn&apos;t be completed — funds returned</Trans>
        </span>
      )
    }
    return {
      icon: (
        <div
          className="text-gray-90 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100/50"
          aria-hidden
        >
          <ArrowDownLeft size={20} />
        </div>
      ),
      label: <Trans>Payout returned</Trans>,
      subtitle,
      sign: '+'
    }
  }

  const isCredit = entry.amountUsd >= 0
  return {
    icon: <div className="h-10 w-10 rounded-full bg-gray-100/50" aria-hidden />,
    label: <Trans>Adjustment</Trans>,
    subtitle: entry.adjustment ? (
      <span className="text-body-3 text-gray-90 truncate">
        <Trans>Reason: {entry.adjustment.reason}</Trans>
      </span>
    ) : null,
    sign: isCredit ? '+' : '−'
  }
}

type LedgerRowProps = {
  entry: LedgerEntry
}

export const LedgerRow = ({ entry }: LedgerRowProps) => {
  const { t } = useLingui()
  const [isExpanded, setIsExpanded] = useState(false)
  const visuals = renderVisuals(entry)
  const expandable = isExpandable(entry)
  const absAmount = Math.abs(entry.amountUsd)
  const onToggleExpand = () => {
    if (!expandable) return
    setIsExpanded((v) => !v)
  }

  return (
    <li className="flex flex-col gap-3 rounded-2xl border border-solid border-gray-100 p-4">
      <div
        className={clsx(
          'grid grid-cols-1 items-center gap-3 max-md:gap-2 md:grid-cols-[auto_1fr_auto_auto_auto]',
          expandable && 'cursor-pointer'
        )}
        onClick={onToggleExpand}
      >
        {visuals.icon}

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-3 font-semibold">
            {visuals.label}
            <div>{visuals.statusPill}</div>
          </div>
          <div className="text-body-3 text-gray-90">{formatDateTime(entry.createdAt)}</div>
          {visuals.subtitle}
        </div>

        <div className="flex flex-col items-end max-md:items-start">
          <div className={clsx('font-semibold')}>
            {visuals.sign}${formatNumber(absAmount, 2)}
          </div>
        </div>

        {expandable ? (
          <button
            type="button"
            className="text-gray-90 flex h-8 w-8 items-center justify-center rounded-full hover:text-gray-100"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand()
            }}
            aria-label={isExpanded ? t`Collapse payout details` : t`Expand payout details`}
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        ) : (
          <div className="w-8" />
        )}
      </div>

      {expandable && isExpanded && entry.payout?.destination.kind === 'crypto' && (
        <PayoutInFlightExpand
          payoutStatus={entry.payout.status}
          cryptoStatus={entry.payout.cryptoStatus}
          txHash={entry.payout.txHash}
        />
      )}
    </li>
  )
}
